import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, Award, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const ResultsPage = ({ onMenuClick }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await api.get('/results');
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Erreur lors du chargement des résultats');
    } finally {
      setLoading(false);
    }
  };

  const calculateAverage = () => {
    if (results.length === 0) return 0;
    const sum = results.reduce((acc, r) => acc + r.score, 0);
    return (sum / results.length).toFixed(1);
  };

  const getGradeColor = (score) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20';
    if (score >= 60) return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
    if (score >= 40) return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20';
    return 'text-rose-600 bg-rose-50 dark:bg-rose-900/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Mes Résultats" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Mes Résultats</h2>
          <p className="text-slate-600 dark:text-slate-400">Vue d'ensemble de vos performances</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{calculateAverage()}%</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Moyenne générale</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{results.length}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Évaluations passées</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">68%</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Taux de réussite</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results List */}
        <Card>
          <CardHeader>
            <CardTitle>Historique des Évaluations</CardTitle>
          </CardHeader>
          <CardContent>
            {results.length > 0 ? (
              <div className="space-y-4">
                {results.map((result) => (
                  <div key={result.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700" data-testid={`result-card-${result.id}`}>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{result.quiz_title}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {new Date(result.graded_at).toLocaleDateString('fr-FR', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      {result.feedback && (
                        <p className="text-sm text-slate-500 mt-2 italic">{result.feedback}</p>
                      )}
                    </div>
                    <div className={`px-4 py-2 rounded-lg font-bold text-2xl ${getGradeColor(result.score)}`}>
                      {result.score}%
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400">Aucun résultat disponible</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ResultsPage;